import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from '@thd-olt-component-react/grid';
import { Alert } from '@thd-olt-component-react/alert';
import { Button } from '@thd-olt-component-react/button';
import { Image } from '@thd-olt-component-react/core-ui';
import { Radio } from '@thd-olt-component-react/checkbox';
import {
  arrayOf,
  params,
  string,
  shape,
  useDataModel
} from '@thd-nucleus/data-sources';

const ChangeStore = (props) => {
  const [storeIndex, setStoreIndex] = useState(0);
  // Normalize incoming stores to ensure `name` attr exists (fallback to storeName)
  const normalizeStores = (list) => (list || []).map((els) => ({ ...els, name: els.name || els.storeName }));
  // eslint-disable-next-line max-len
  const [stores, setStores] = useState(normalizeStores(props.stores)); // eslint-disable-line react/destructuring-assignment
  // storeLocation treated as postalCode if user entered 5 digit number otherwise will treat as store Id.
  const [storeLocation, setStoreLocation] = useState(props.stores[0]?.address?.postalCode || ''); // eslint-disable-line

  const { onChangeClick } = props;

  const [getStoreSearch, { data, loading, error }] = useDataModel('storeSearch', { opts: {} }, { lazy: true });
  const [getStoreDetails, storeResponse] = useDataModel('storeDetails', { opts: {} }, { lazy: true });

  const onSubmit = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }
    if (storeLocation.length === 5) {
      getStoreSearch({
        variables: {
          storeSearchInput: storeLocation,
          pagesize: '5',
          radius: '50'
        }
      });
    } else {
      getStoreDetails({
        variables: {
          storeId: storeLocation
        }
      });
    }
  }, [storeLocation, getStoreDetails, getStoreSearch]);

  useEffect(() => {
    if (!storeResponse || storeResponse.loading || storeResponse.error || !storeResponse.data) {
      return;
    }
    const zipCode = storeResponse?.data?.storeDetails?.address?.postalCode;
    if (!zipCode) {
      setStoreIndex(0);
      setStores(null);
      return;
    }
    getStoreSearch({
      variables: {
        storeSearchInput: zipCode,
        pagesize: '5',
        radius: '50'
      }
    });

  }, [storeResponse, getStoreSearch]);

  useEffect(() => {
    if ((error && !data) || !data || loading) {
      return;
    }
    const searchStores = data?.storeSearch?.stores || data?.storeSearch || [];
    setStoreIndex(0);
    const normalized = normalizeStores(searchStores);
    setStores(normalized.length === 0 ? null : normalized);

  }, [data, error, loading]);

  const onChangePreferredStore = () => {
    if (!storeIndex && storeIndex !== 0) {
      onChangeClick();
    } else {
      window.LIFE_CYCLE_EVENT_BUS.trigger('product-results.change-store', stores[storeIndex]);
    }
  };

  const onChangeStoreIndex = (index) => {
    setStoreIndex(index);
  };

  const onUpdateZipCode = (event) => {
    setStoreLocation(event.target.value);
  };

  const onCancel = () => {
    setStoreIndex(0);
    onChangeClick();
  };

  return (
    <div className="change-store">
      <Row>
        <form id="prc-store-search-form" className="change-store__form" onSubmit={onSubmit}>
          <div className="change-store__input">
            <input
              className="form-input__field"
              type="text"
              defaultValue={storeLocation}
              onChange={onUpdateZipCode}
            />
            <Button
              className="change-store__button"
              inline
              type="submit"
              form="prc-store-search-form"
            >
              <Image
                alt="magnifying glass"
                className="change-store__magnifying-glass"
                src="https://assets.thdstatic.com/images/v1/magnify-symbol-white.svg"
                height="40"
                width="40"
              />
            </Button>
          </div>
        </form>
      </Row>
      {!stores && (
        <Row>
          <Col>
            <Alert
              inline
              message="Please enter a valid ZIP Code or store number"
            />
          </Col>
        </Row>
      )}
      {stores && stores.length > 0 && (
        <>
          <Row>
            <div className="change-store__radio-wrapper">
              <div className="change-store__radio-wrapper-title">Choose your preferred store: </div>
              <Radio.Group className="change-store__radio-group">
                {stores.map((store, index) => {
                  const { distance, storeId, name } = store;
                  const parsedDistance = parseFloat(distance);
                  const miles = distance && !Number.isNaN(parsedDistance)
                    ? (Math.round(parsedDistance * 100) / 100)
                    : '0.0';
                  const label = `${name} (${miles} mi)`;
                  return (
                    <Radio
                      className="change-store__radio-button"
                      id={`${storeId}-${index}`}
                      key={`${storeId}-${index}`}
                      onChange={() => onChangeStoreIndex(index)}
                      value={index.toString()}
                      checked={index === 0}
                      formName="change-store-selector"
                    >
                      {label}
                    </Radio>
                  );
                })}
              </Radio.Group>
            </div>
          </Row>
          <Row className="change-store__buttons">
            <div className="change-store__save">
              <Button onClick={onChangePreferredStore}>Save</Button>
            </div>
            <div className="change-store__cancel">
              <Button outline onClick={onCancel}>Cancel</Button>
            </div>
          </Row>
        </>
      )}
    </div>
  );
};

ChangeStore.propTypes = {
  onChangeClick: PropTypes.func.isRequired,
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      storeId: PropTypes.string,
      name: PropTypes.string,
      phone: PropTypes.string,
      address: PropTypes.shape({
        postalCode: PropTypes.string
      })
    })
  ).isRequired,
};

ChangeStore.displayName = 'ChangeStore';

const storeDetail = {
  storeId: string(),
  name: string(),
  phone: string(),
  distance: string(),
  address: shape({
    postalCode: string()
  })
};

ChangeStore.dataModel = {
  storeSearch: params({
    storeSearchInput: string().isRequired(),
    pagesize: string(),
    radius: string().isRequired(),
  }).shape({
    stores: arrayOf(storeDetail)
  }),
  storeDetails: params({
    storeId: string()
  }).shape(storeDetail)
};

export { ChangeStore };
